import { useContext, useMemo } from 'react';

import VolatilitySmileWidget from '../v2/widgets/volatility-smile';
import VolatilitySmileComparisonWidget from '../v2/widgets/volatility-smile-comparison';
import TermStructureWidget from '../v2/widgets/term-structure';
import TermStructureComparisonWidget from '../v2/widgets/term-structure-comparison';
import ModelVolatilitySurfaceWidget from '../v2/widgets/model-volatility-surface';
import ScatterPlotWidget from '../v2/widgets/scatter-plot';
import VolatilityConesWidget from '../v2/widgets/volatility-cones';
import VolatilityHeatmapWidget from '../v2/widgets/volatility-heatmap';
import FootprintVolatilityCurvesWidget from '../v2/widgets/footprint-volatility-curves';

import { GalleryContext } from '../../providers/gallery-provider';
import { CustomWidget, WidgetKeys } from '../../providers/model/custom-widget';
import OpenInterestWidget from '../v2/widgets/open-interest';
import { WidgetProvider } from '../v2/widget-renderer/widget-provider';

type Actions = {
  handleMaximizeWidget: (cardKey: string) => void;
  handleDuplicateWidget: (cardKey: string) => void;
  handleCloseWidget: (cardKey: string) => void;
};

type RenderParams = {
  providerKey: string;
  widget: CustomWidget;
};
type RenderWrapperParams = {
  gridKey: string;
};

export const useGalleryWidgets = () => {
  const { toggleRowExpand, currency } = useContext(GalleryContext);

  const actions: Actions = useMemo(
    () => ({
      handleMaximizeWidget: (cardKey: string) => toggleRowExpand(cardKey),
      handleDuplicateWidget: () => {},
      handleCloseWidget: () => {},
    }),
    [toggleRowExpand],
  );

  const widgetByGridKey: Record<string, CustomWidget> = useMemo(
    () => ({
      'vol-smile': {
        id: 'vol-smile',
        key: WidgetKeys.VolatilitySmile,
        title: 'Vol Smile',
      },
      'vol-smile-compare': {
        id: 'vol-smile-compare',
        key: WidgetKeys.VolSmileComparison,
        title: 'Vol Smile Comparison',
      },
      'term-structure': {
        id: 'term-structure',
        key: WidgetKeys.TermStructure,
        title: 'Term Structure',
      },
      'term-structure-compare': {
        id: 'term-structure-compare',
        key: WidgetKeys.TermStructureComparison,
        title: 'Term Structure Comparison',
      },
      'model-volatility-surface': {
        id: 'model-volatility-surface',
        key: WidgetKeys.ModelVolatilitySurface,
        title: 'Volatility Surface',
      },
      'scatter-plot': {
        id: 'scatter-plot',
        key: WidgetKeys.ScatterPlot,
        title: 'Scatter Plot',
      },
      'volatility-heatmap': {
        id: 'volatility-heatmap',
        key: WidgetKeys.VolatilityHeatmap,
        title: 'Volatility Heatmap',
      },
      'volatility-cones': {
        id: 'volatility-cones',
        key: WidgetKeys.VolatilityCones,
        title: 'Volatility Cones',
      },
      'footprint-volatility': {
        id: 'footprint-volatility',
        key: WidgetKeys.FootprintVolatility,
        title: 'Footprint Volatility Curves',
      },
      'open-interest': {
        id: 'open-interest',
        key: WidgetKeys.OpenInterest,
        title: 'Open Interest',
      },
    }),
    [],
  );

  const render = ({ widget, providerKey }: RenderParams): JSX.Element | null => {
    switch (widget.key) {
      case WidgetKeys.VolatilitySmile:
        return (
          <VolatilitySmileWidget
            providerKey={providerKey}
            actions={actions}
            widget={widget}
            permissions={{ canDelete: false, canDuplicate: false }}
          />
        );
      case WidgetKeys.VolSmileComparison:
        return (
          <VolatilitySmileComparisonWidget
            providerKey={providerKey}
            actions={actions}
            widget={widget}
            permissions={{ canDelete: false, canDuplicate: false }}
          />
        );
      case WidgetKeys.TermStructure:
        return (
          <TermStructureWidget
            providerKey={providerKey}
            actions={actions}
            widget={widget}
            permissions={{ canDelete: false, canDuplicate: false }}
          />
        );
      case WidgetKeys.TermStructureComparison:
        return (
          <TermStructureComparisonWidget
            providerKey={providerKey}
            actions={actions}
            widget={widget}
            permissions={{ canDelete: false, canDuplicate: false }}
          />
        );
      case WidgetKeys.ModelVolatilitySurface:
        return (
          <ModelVolatilitySurfaceWidget
            providerKey={providerKey}
            actions={actions}
            widget={widget}
            permissions={{ canDelete: false, canDuplicate: false }}
          />
        );
      case WidgetKeys.ScatterPlot:
        return (
          <ScatterPlotWidget
            providerKey={providerKey}
            actions={actions}
            widget={widget}
            permissions={{ canDelete: false, canDuplicate: false }}
          />
        );
      case WidgetKeys.VolatilityCones:
        return (
          <VolatilityConesWidget
            providerKey={providerKey}
            actions={actions}
            widget={widget}
            permissions={{ canDelete: false, canDuplicate: false }}
          />
        );
      case WidgetKeys.VolatilityHeatmap:
        return (
          <VolatilityHeatmapWidget
            providerKey={providerKey}
            actions={actions}
            widget={widget}
            permissions={{ canDelete: false, canDuplicate: false }}
          />
        );
      case WidgetKeys.FootprintVolatility:
        return (
          <FootprintVolatilityCurvesWidget
            providerKey={providerKey}
            actions={actions}
            widget={widget}
            permissions={{ canDelete: false, canDuplicate: false }}
          />
        );
      case WidgetKeys.OpenInterest:
        return (
          <OpenInterestWidget
            providerKey={providerKey}
            actions={actions}
            widget={widget}
            permissions={{ canDelete: false, canDuplicate: false }}
          />
        );
      default:
        return null;
    }
  };

  const renderWrapper = ({ gridKey }: RenderWrapperParams) => {
    const widget = widgetByGridKey[gridKey];
    if (!widget) return null;

    const providerKey = `gallery_${widget.id || widget.key}`;

    return (
      <WidgetProvider providerKey={providerKey} defaultToken={currency}>
        {render({ widget, providerKey })}
      </WidgetProvider>
    );
  };

  return {
    renderWidget: renderWrapper,
    hasWidget: (gridKey: string) => Boolean(widgetByGridKey[gridKey]),
  };
};
